import QRCode from 'qrcode';
import { useEffect, useRef, useState, type ReactElement } from 'react';

const DEFAULT_QR_SIZE_PX = 120;

type PartyQrCodeProps = {
  readonly path: string;
  readonly className?: string;
  readonly size?: number;
  /** A code held up to a room has to survive the small screens the inline one hides on. */
  readonly alwaysVisible?: boolean;
  readonly caption?: string;
  readonly alt?: string;
};

// Hidden below the `md` breakpoint by default: on a phone the visitor is already
// on, or a tap away from, the page this points to.
export const PartyQrCode = ({
  path,
  className = '',
  size = DEFAULT_QR_SIZE_PX,
  alwaysVisible = false,
  caption = 'Scan to follow',
  alt = 'QR code linking to the public party view',
}: PartyQrCodeProps): ReactElement | null => {
  const [dataUrl, setDataUrl] = useState<string | undefined>(undefined);
  const draftIsCancelled = useRef(false);

  useEffect(() => {
    draftIsCancelled.current = false;

    const generate = async (): Promise<void> => {
      try {
        // Resolved against `location.origin`, so the same build points at
        // itself on localhost, staging, or prod.
        const target = new URL(path, location.origin).href;
        const url = await QRCode.toDataURL(target, { margin: 1, width: size });
        if (!draftIsCancelled.current) {
          setDataUrl(url);
        }
      } catch {
        // No code is better than a broken one — the page still works without it.
      }
    };

    void generate();

    return (): void => {
      draftIsCancelled.current = true;
    };
  }, [path, size]);

  if (dataUrl === undefined) {
    return null;
  }

  const visibility = alwaysVisible ? 'flex' : 'hidden md:flex';

  return (
    <div
      className={`bg-surface rounded-card border-subtle ${visibility} shrink-0 flex-col items-center gap-2 border p-3 ${className}`}
    >
      <img src={dataUrl} alt={alt} width={size} height={size} className="max-w-full" />
      {caption === '' ? null : (
        <p className="text-muted text-center text-[10px] tracking-widest uppercase">{caption}</p>
      )}
    </div>
  );
};
